import React, { useState, useCallback } from 'react';
import StageModale from './StageModale';

/**
 * SUIVI DE STAGE — les décisions sur la fiche de liaison (lot 3.3 ; E173, v85)
 * ===========================================================================
 * E173 (2026-10-21, maquette rév. 8) : DEUX moments de décision, le même bloc à trois cartes —
 *   · `etape="responsable"` — ③ la VALIDATION DU RESPONSABLE, sur la fiche de l'ÉTUDIANT, avant l'envoi au maître :
 *     Valider (aucun courriel) · Renvoyer à l'étudiant (commentaire OBLIGATOIRE, nouveau lien) · Refuser le stage.
 *     Réservée aux responsables des stages et aux administrateurs (le serveur le vérifie : `droits.peut_valider_etudiant`).
 *   · `etape="administrative"` — ⑥ la VALIDATION ADMINISTRATIVE (ex « Validation DPT »), par l'assistante, après le
 *     maître : Valider · Renvoyer au maître · Refuser (toujours réservé au responsable). Si le maître a MODIFIÉ la
 *     saisie de l'étudiant, un encart le dit — il informe, il ne bloque pas (décision de Bertrand).
 * Spec     : doc/modules/suivi_de_stage.md § Workflow étape ⑤, tests S20 → S24
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#decision-fiche-but3)
 *
 * ── TROIS CAS, TROIS CARTES, TROIS COULEURS ────────────────────────────────
 * La maquette ne dessine pas une liste d'actions mais **trois cartes** dans un
 * encadré ambre — verte, jaune, rouge — chacune avec sa propre explication et
 * son bouton pleine largeur. Ce n'est pas de l'ornement : ces trois gestes
 * n'ont rien de comparable. L'un fait avancer le dossier, l'autre le renvoie
 * en arrière, le troisième l'efface. Les aligner comme trois options d'un menu
 * les ferait se ressembler à l'œil alors qu'ils diffèrent par leurs
 * conséquences.
 *
 * ── ⚠️ CE QUE CET ÉCRAN NE DEMANDE PAS ─────────────────────────────────────
 * **Aucun mode de convention** (écart E63). La spec est explicite — *« la
 * validation ne choisit PAS le mode de convention ; ce choix revient aux
 * assistantes à l'étape ⑥ »* — et la maquette ne dessine aucun sélecteur ici.
 * Seul le test S21 en demandait un ; il datait d'avant cette révision.
 *
 * ── ⚠️ LE REFUS SE CONFIRME, LES DEUX AUTRES NON ───────────────────────────
 * Règle 6 : jamais d'`alert` ni de `confirm`, et un geste destructeur s'annonce
 * par un **récapitulatif de ce qu'il détruit**, pas par « Êtes-vous sûr ? ».
 *
 * Le refus efface la fiche de liaison et tue tous les liens en cours : il passe
 * donc par une modale qui le DIT. Valider et renvoyer, eux, se confirment
 * d'eux-mêmes — ils ouvrent la fenêtre du message qui part, ce qui est le
 * meilleur des récapitulatifs : on voit ce qu'on envoie avant de l'envoyer.
 */

/**
 * ⚠️ LE MOTIF EST OBLIGATOIRE — ET C'EST TOUT (défaut du 2026-09-25).
 *
 * J'avais posé un plancher de **dix caractères**, que personne n'avait demandé :
 * la spec dit « commentaire obligatoire », pas « d'au moins dix signes ». Bertrand
 * a écrit un motif court, le bouton est resté gris, **et rien ne disait pourquoi**
 * — l'explication était dans une infobulle qu'il faut songer à survoler.
 *
 * Deux fautes en une : une règle inventée, et une règle qui échoue en silence.
 * Le plancher disparaît, et ce qui reste se VOIT sous le champ.
 */

function Carte({ couleur, icone, titre, children }) {
    return (
        <div className={'stg-cas stg-cas-' + couleur}>
            <div className="stg-cas-titre"><i className={'fa-solid ' + icone} /> {titre}</div>
            {children}
        </div>
    );
}

function StageBlocDecision({ dossier, enCours, onValider, onRenvoyer, onRefuser, etape = 'administrative' }) {
    const responsable = etape === 'responsable';
    // ③ : valider et renvoyer sont réservés au responsable (et aux administrateurs) — la raison s'affiche.
    const peutDecider = !responsable || dossier.droits?.peut_valider_etudiant === true;
    const motifDecision = responsable ? (dossier.droits?.motif_validation_etudiant || null) : null;
    const modifications = dossier.fiche?.modifications_maitre || [];
    /**
     * ⚠️ LE DROIT DE REFUSER VIENT DU SERVEUR (écart E67).
     *
     * On ne le recalcule pas ici à partir du profil : le serveur est le seul à
     * connaître la liste des responsables du département, et c'est LUI qui
     * refusera. Deux règles pour la même chose finiraient par diverger — et
     * l'écart se verrait le jour où un bouton actif renverrait un 403.
     *
     * ⚠️ Défaut d'un jour trouvé par Bertrand le 2026-09-25 : le bouton
     * s'activait pour n'importe quel compte ayant le droit de modifier, alors
     * que l'encadré juste au-dessus annonce « Action réservée au responsable
     * des stages ». Un écran qui dit une règle et ne l'applique pas est pire
     * que pas de règle : on croit qu'elle protège.
     */
    const peutRefuser = dossier.droits ? dossier.droits.peut_refuser === true : false;
    const motifDroit = dossier.droits?.motif || null;
    const [commentaireRenvoi, setCommentaireRenvoi] = useState('');
    const [motifRefus, setMotifRefus] = useState('');
    const [confirmeRefus, setConfirmeRefus] = useState(false);

    const motifPret = motifRefus.trim().length > 0;

    const refuser = useCallback(() => {
        setConfirmeRefus(false);
        onRefuser(motifRefus.trim());
        setMotifRefus('');
    }, [motifRefus, onRefuser]);

    const renvoyer = useCallback(() => {
        onRenvoyer(commentaireRenvoi.trim() || null);
        setCommentaireRenvoi('');
    }, [commentaireRenvoi, onRenvoyer]);
    // ③ : le commentaire est OBLIGATOIRE (il dit à l'étudiant quoi compléter) ; ⑥ : facultatif, comme avant.
    const commentairePret = !responsable || commentaireRenvoi.trim().length > 0;

    return (
        <div className="stg-decision">
            <div className="stg-decision-titre">
                <i className={'fa-solid ' + (responsable ? 'fa-user-check' : 'fa-stamp')} />
                <span>{responsable ? '③ Validation du responsable — décision requise' : '⑥ Validation administrative — décision requise'}</span>
                <span className="badge badge-bloquant">⏳ En attente</span>
            </div>
            {!responsable && modifications.length > 0 && (
                <div className="stg-cas-exigence stg-decision-modifiee">
                    <i className="fa-solid fa-pen" /> <span>Fiche <b>modifiée par le maître</b> ({modifications.length} information{modifications.length > 1 ? 's' : ''}, voir ⑤) —
                    à vous de voir s'il faut valider ou renvoyer. Les responsables des stages ont été prévenus.</span>
                </div>
            )}
            {!peutDecider && (
                <div className="stg-cas-exigence"><i className="fa-solid fa-lock" /> <span>{motifDecision}</span></div>
            )}

            {/* ── CAS 1 ─────────────────────────────────────────────────── */}
            <Carte couleur="valider" icone="fa-check" titre={responsable ? "Valider la fiche de l'étudiant" : 'Valider la fiche'}>
                {responsable ? (
                    <p>
                        Le contenu convient : l'assistante peut l'<b>envoyer au maître de stage</b> (④).
                        Aucun courriel ne part.
                    </p>
                ) : (
                    <p>
                        La fiche est complète. Le dossier passe à la <b>convention</b> (⑦) — le mode de
                        remise est choisi à ce moment-là. Un courriel de confirmation est proposé
                        (maître de stage et étudiant, assistantes en copie).
                    </p>
                )}
                <button type="button" className="btn btn-green btn-sm stg-cas-bouton"
                        disabled={enCours || !peutDecider} onClick={onValider}>
                    <i className="fa-solid fa-check" /> {responsable ? "Valider — prête pour l'envoi au maître" : 'Valider la fiche de liaison'}
                </button>
            </Carte>

            {/* ── CAS 2 ─────────────────────────────────────────────────── */}
            <Carte couleur="renvoyer" icone="fa-rotate-right" titre={responsable ? "Renvoyer à l'étudiant" : 'Renvoyer le lien au maître de stage'}>
                {responsable ? (
                    <p>
                        <b>Un nouveau lien est préparé pour l'étudiant</b> (l'ancien ne marche plus), avec votre
                        commentaire. Ce qu'il avait saisi est conservé.
                    </p>
                ) : (
                    <p>
                        La fiche contient une erreur — <b>un nouveau lien est généré, l'ancien est
                        invalidé</b>.
                    </p>
                )}
                <textarea className={'input-sm' + (responsable ? ' stg-cas-obligatoire' : '')} value={commentaireRenvoi}
                          disabled={enCours || !peutDecider}
                          onChange={(e) => setCommentaireRenvoi(e.target.value)}
                          placeholder={responsable
                              ? "Commentaire (obligatoire) — Ex : Précisez les missions et le service d'accueil."
                              : "Commentaire (facultatif) — Ex : Merci de corriger l'adresse email de l'entreprise."} />
                {responsable && peutDecider && !commentairePret && (
                    <div className="stg-cas-exigence">
                        <i className="fa-solid fa-circle-info" /> <span>Écrivez le commentaire pour activer le bouton : il dit à
                        l'étudiant <b>quoi compléter</b>.</span>
                    </div>
                )}
                <button type="button" className="btn btn-sm stg-cas-bouton stg-btn-ambre"
                        disabled={enCours || !peutDecider || !commentairePret} onClick={renvoyer}>
                    <i className="fa-solid fa-rotate-right" /> {responsable ? "Renvoyer à l'étudiant avec commentaire" : 'Renvoyer le lien au maître'}
                </button>
            </Carte>

            {/* ── CAS 3 ─────────────────────────────────────────────────── */}
            <Carte couleur="refuser" icone="fa-ban" titre="Refuser le stage">
                {/* ⚠️ L'avertissement de la maquette, mot pour mot : il dit ce que
                    le geste ENTRAÎNE, pas ce qu'il est. C'est la différence entre
                    « Refuser ? » et « l'étudiant devra retrouver une entreprise ». */}
                <div className="stg-cas-alerte">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>
                        Action réservée au responsable des stages. Le dossier sera réinitialisé —
                        l'étudiant doit trouver une nouvelle entreprise. <b>L'historique est
                        conservé.</b>
                    </span>
                </div>
                <textarea className="input-sm stg-cas-obligatoire" value={motifRefus}
                          disabled={enCours || !peutRefuser}
                          onChange={(e) => setMotifRefus(e.target.value)}
                          placeholder="Motif du refus (obligatoire) — Ex : Mission non conforme aux objectifs BUT1." />
                {/* ⚠️ LA RAISON DU BOUTON GRIS SE VOIT, elle n'est plus cachée dans
                    une infobulle. Un bouton désactivé sans explication visible, ce
                    n'est pas « pas encore » : c'est « en panne » — et on cherche
                    la panne ailleurs (défaut trouvé par Bertrand le 2026-09-25).
                    ⚠️ Le droit passe AVANT le motif : dire « écrivez le motif » à
                    quelqu'un qui n'a pas le droit de refuser l'enverrait écrire
                    pour rien. */}
                {!peutRefuser ? (
                    <div className="stg-cas-exigence">
                        <i className="fa-solid fa-lock" /> <span>{motifDroit}</span>
                    </div>
                ) : !motifPret && (
                    <div className="stg-cas-exigence">
                        <i className="fa-solid fa-circle-info" /> <span>Écrivez le motif pour activer le
                        bouton : c'est la seule trace du <b>pourquoi</b>, et elle ne s'efface pas.</span>
                    </div>
                )}
                <button type="button" className="btn btn-danger btn-sm stg-cas-bouton"
                        disabled={enCours || !peutRefuser || !motifPret}
                        onClick={() => setConfirmeRefus(true)}>
                    <i className="fa-solid fa-ban" /> Refuser le stage — réinitialiser le dossier
                </button>
            </Carte>

            {/* ── Le récapitulatif AVANT le refus ───────────────────────── */}
            {confirmeRefus && (
                <StageModale
                    titre="Refuser le stage"
                    icone="fa-ban" ton="danger"
                    libelleValider="Refuser et réinitialiser"
                    onValider={refuser}
                    libelleAnnuler="Annuler"
                    onFermer={() => setConfirmeRefus(false)}
                >
                    <p style={{ marginTop: 0 }}>
                        Le dossier de <b>{dossier.etudiant?.nom} {dossier.etudiant?.prenom}</b> va
                        repartir de zéro. Voici précisément ce qui se passe :
                    </p>
                    {/* ⚠️ On ÉNUMÈRE. « Le dossier sera réinitialisé » ne dit pas que
                        la fiche disparaît ni que les liens meurent — or ce sont les
                        deux choses qu'on ne peut pas défaire. */}
                    <ul className="stg-recap">
                        <li>La <b>fiche de liaison</b> saisie par l'étudiant et corrigée par le
                            maître de stage est <b>effacée</b>.</li>
                        <li>Tous les <b>liens en cours meurent</b> : le maître de stage de cette
                            entreprise ne pourra plus rien ouvrir.</li>
                        <li>Le dossier revient en <b>« Envoyer le lien »</b> : l'étudiant pourra
                            saisir une nouvelle fiche.</li>
                        {/* E137 (2026-10-06) : le refus a désormais son message, comme les CAS 1 et 2. */}
                        <li>Le <b>message à l'étudiant</b> vous est ensuite proposé : ce motif et le
                            lien d'une <b>nouvelle fiche</b>, à relire avant l'envoi.</li>
                        <li>Le refus et son motif restent dans l'<b>historique</b>, qui ne
                            s'efface pas.</li>
                    </ul>
                    <div className="stg-encart stg-encart-info" style={{ marginBottom: 0 }}>
                        <i className="fa-solid fa-quote-left" />
                        <span>{motifRefus.trim()}</span>
                    </div>
                </StageModale>
            )}
        </div>
    );
}

export default StageBlocDecision;
